/**
 * `<Panel>` — the CONTENT renderer. Renders a panel's elements and nothing else.
 *
 * It deliberately owns no presentation behaviour: no portal, no scrim, no focus
 * trap, no Escape handling, no queueing. Those belong to a presenter
 * (`<PanelHost>` for modals). Dropping `<Panel>` straight into a page IS the
 * inline presentation — that is the whole point of separating the two.
 *
 * Keeping them separate is also an accessibility guard: a modal must trap focus
 * and a heading-labelled inline region must NOT. If one component did both, the
 * inline case would inherit dialog behaviour and quietly break keyboard
 * navigation on the page around it. Here the semantics are chosen by the caller
 * — the presenter passes `role="dialog"` / `aria-modal`, and the default is a
 * labelled `region`.
 *
 * The renderer never sets `innerHTML`, never fetches, and never navigates.
 */
import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { parsePanelAction } from './action';
import { ensurePanelStyles } from './styles';
import { substitutePlaceholders } from './text';
import type {
  PanelAction,
  PanelContent,
  PanelButtonElement,
  PanelElement,
  PanelImageRef,
  PanelItem,
  PanelRenderContext,
} from './types';

export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  definition: PanelContent;
  context?: PanelRenderContext;
  /**
   * Called when the user activates a button whose action parses. A button with
   * an unparseable action is rendered disabled and can never fire this.
   */
  onAction?: (action: PanelAction) => void;
  /** Width preset. Only meaningful when a presenter constrains the panel. */
  size?: 'small' | 'medium' | 'large';
  /**
   * When provided, renders a close affordance in the corner. Presenters pass
   * this; inline panels usually don't have one.
   */
  onDismiss?: () => void;
  /** Accessible name for the close affordance. */
  dismissLabel?: string;
  /**
   * Which heading element the title renders as. Default `2`.
   *
   * Matters for INLINE panels: the panel joins the host page's heading outline,
   * and a hard-coded `<h2>` inside a section whose own heading is an `h3` would
   * break the document hierarchy. Modal panels can leave the default — a dialog
   * is its own context. Visual size is unaffected (the title is styled by
   * class, not by element).
   */
  headingLevel?: 2 | 3 | 4;
}

/**
 * Resolve an image reference to a URL.
 *
 * Some paths hand us a reference the host must resolve (an arche asset id; a
 * storage key needing a presigned URL) — that is what `context.resolveImage` is
 * for, and it is the only mechanism enabled by default.
 *
 * Other paths have ALREADY resolved it: the bridge asset presigner rewrites
 * storage keys to signed URLs in place on the way out of `get_config`, so by
 * render time the field holds an `https:` URL. Those consumers opt in with
 * `context.allowResolvedUrls` — see the doc on that field for why accepting such
 * URLs unconditionally would make this renderer the weakest link in the
 * write-time root lock.
 *
 * Anything unresolved renders nothing: a missing image must never break the rest
 * of the panel, and must never fall back to "probably a URL".
 */
function resolveImageUrl(
  ref: PanelImageRef,
  context: PanelRenderContext | undefined,
): string | undefined {
  const resolved = context?.resolveImage?.(ref);
  if (resolved) return resolved;
  if (!context?.allowResolvedUrls || ref.kind !== 'storageKey') return undefined;
  try {
    return new URL(ref.imageKey).protocol === 'https:' ? ref.imageKey : undefined;
  } catch {
    return undefined;
  }
}

/** Overridable via `context.formatAmount` — see the note on that field. */
function defaultFormatAmount(amount: number): string {
  return `×${amount}`;
}

/**
 * Look up a named item list.
 *
 * The own-property check is load-bearing, not defensive habit: the slot grammar
 * permits `constructor`, `toString` and friends, so a plain `itemsBySlot[slot]`
 * would reach `Object.prototype` and hand back a function that the render path
 * below would then call `.map` on — a crash from an authored string. Mirrors the
 * same guard in `config-structured-schemas.ts`. The `Array.isArray` check makes
 * the failure mode "renders empty" for any other surprise, which is the same
 * degrade as an unfilled slot.
 */
function resolveSlot(
  context: PanelRenderContext | undefined,
  slot: string,
): PanelItem[] | undefined {
  const bySlot = context?.itemsBySlot;
  if (!bySlot || !Object.prototype.hasOwnProperty.call(bySlot, slot)) return undefined;
  const list = bySlot[slot];
  return Array.isArray(list) ? list : undefined;
}

function ImageElement({
  src,
  alt,
  maxHeight,
}: {
  src: string | undefined;
  alt: string | undefined;
  maxHeight: number | undefined;
}): ReactNode {
  if (!src) return null;
  return (
    <img
      className="fias-panel__image"
      src={src}
      // Decorative art with no author-supplied alt is hidden from assistive
      // tech (empty alt) rather than announced as a filename.
      alt={alt ?? ''}
      style={maxHeight != null ? { maxHeight: `${maxHeight}px` } : undefined}
    />
  );
}

function renderElement(
  element: PanelElement,
  key: string,
  context: PanelRenderContext | undefined,
): ReactNode {
  switch (element.kind) {
    case 'text': {
      const style = element.style ?? 'body';
      const align = element.align ?? 'center';
      return (
        <p
          key={key}
          className={`fias-panel__text fias-panel__text--${style} fias-panel__text--${align}`}
        >
          {substitutePlaceholders(element.text, context?.values)}
        </p>
      );
    }
    case 'image':
      return (
        <ImageElement
          key={key}
          src={resolveImageUrl({ kind: 'storageKey', imageKey: element.imageKey }, context)}
          alt={element.alt}
          maxHeight={element.maxHeight}
        />
      );
    case 'assetImage':
      return (
        <ImageElement
          key={key}
          src={resolveImageUrl({ kind: 'asset', assetId: element.assetId }, context)}
          alt={element.alt}
          maxHeight={element.maxHeight}
        />
      );
    case 'items':
    case 'slottedItems': {
      // `slottedItems` draws a NAMED list; `items` draws the default one. A slot
      // the caller never filled is NOT an error — it takes the same empty path as
      // an empty default list, so a panel authored for four reward tiers degrades
      // cleanly for a player who earned one. It must never fall back to the
      // default list: that would show rewards the player did not earn.
      const items =
        (element.kind === 'slottedItems' ? resolveSlot(context, element.slot) : context?.items) ??
        [];
      if (items.length === 0) {
        return element.emptyText ? (
          <p key={key} className="fias-panel__items-empty">
            {element.emptyText}
          </p>
        ) : null;
      }
      const showIcons = element.showIcons ?? true;
      const iconSize = element.iconSize ?? 'medium';
      return (
        <ul key={key} className="fias-panel__items">
          {items.map((item, index) => (
            <li className="fias-panel__item" key={`${item.label}-${index}`}>
              {showIcons && item.icon ? (
                <span className="fias-panel__item-icon" aria-hidden>
                  {item.icon}
                </span>
              ) : null}
              {showIcons && !item.icon && item.imageUrl ? (
                <img
                  className={`fias-panel__item-icon fias-panel__item-icon--${iconSize}`}
                  src={item.imageUrl}
                  alt=""
                />
              ) : null}
              <span>{item.label}</span>
              {item.amount != null ? (
                <span className="fias-panel__item-amount">
                  {(context?.formatAmount ?? defaultFormatAmount)(item.amount)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }
    case 'spacer':
      return (
        <div key={key} className={`fias-panel__spacer--${element.size ?? 'medium'}`} aria-hidden />
      );
    default:
      // Unknown kinds are SKIPPED, never thrown on: a shipped renderer may be
      // older than the definition it is handed. The write validator is the
      // place that rejects unknown kinds.
      return null;
  }
}

/**
 * Consecutive buttons render as one row. Grouping is visual only — it never
 * reorders elements, so a button between two texts still renders between them.
 *
 * The button groups are typed as `PanelButtonElement[]`, not `PanelElement[]`, so
 * the render path below needs no re-narrowing — the array shape carries the
 * guarantee that only buttons are in it.
 */
type PanelElementGroup = PanelElement | PanelButtonElement[];

function groupElements(elements: PanelElement[]): PanelElementGroup[] {
  const groups: PanelElementGroup[] = [];
  for (const element of elements) {
    const last = groups[groups.length - 1];
    if (element.kind === 'button') {
      if (Array.isArray(last)) last.push(element);
      else groups.push([element]);
    } else {
      groups.push(element);
    }
  }
  return groups;
}

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  {
    definition,
    context,
    onAction,
    size,
    onDismiss,
    dismissLabel = 'Close',
    headingLevel = 2,
    className,
    role = 'region',
    ...rest
  },
  forwardedRef,
) {
  const Heading = `h${headingLevel}` as const;
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Layout effect, not a passive one: passive effects run AFTER paint, so the
  // first panel ever mounted in a document could flash one unstyled frame.
  // A layout effect runs after the DOM is committed (the ref is attached, so we
  // can reach the panel's OWN document — inside a plugin iframe that is not the
  // top document) but before the browser paints. Not `useInsertionEffect`
  // because that fires before refs are attached, which would lose exactly the
  // ownerDocument targeting this injection exists for.
  useLayoutEffect(() => {
    ensurePanelStyles(rootRef.current?.ownerDocument);
  }, []);

  const setRef = (node: HTMLDivElement | null) => {
    rootRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  const classes = ['fias-panel'];
  if (size) classes.push(`fias-panel--${size}`);
  if (className) classes.push(className);

  return (
    <div {...rest} ref={setRef} className={classes.join(' ')} role={role} aria-labelledby={titleId}>
      {onDismiss ? (
        <button type="button" className="fias-panel__close" onClick={onDismiss}>
          <span aria-hidden>×</span>
          <span className="fias-panel__sr-only">{dismissLabel}</span>
        </button>
      ) : null}

      <Heading className="fias-panel__title" id={titleId}>
        {definition.title}
      </Heading>

      {groupElements(definition.elements).map((group, index) => {
        if (!Array.isArray(group)) return renderElement(group, `el-${index}`, context);
        return (
          <div className="fias-panel__buttons" key={`btn-${index}`}>
            {group.map((button, buttonIndex) => {
              const parsed = parsePanelAction(button.action);
              return (
                <button
                  type="button"
                  key={`btn-${index}-${buttonIndex}`}
                  className={`fias-panel__button fias-panel__button--${button.variant ?? 'primary'}`}
                  // An action the grammar rejects makes the button inert rather
                  // than removing it: the layout the author intended is
                  // preserved, and the bad action cannot fire.
                  disabled={parsed === null}
                  onClick={parsed === null ? undefined : () => onAction?.(parsed)}
                >
                  {button.label}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
});
